<template>
    <div
        v-show="showRecaptcha"
        class="space-y-6"
    >
        <div class="mb-4">
            <span class="text-lg font-medium text-gray-600">{{
                t('passCaptcha')
            }}</span>
        </div>

        <rp-recaptcha
            v-if="showRecaptcha"
            :lang="recaptchaLang"
            @token="recaptchaToken = $event"
        />

        <prime-button
            :label="t('continue')"
            class="w-full"
            :disabled="recaptchaDisabled"
            :loading="isVerifyingGuest"
            @click="submitGuest"
        />
    </div>

    <div v-if="showRecaptcha" />

    <div
        v-else-if="token === undefined"
        class="px-3"
    >
        <form @submit="onSubmit">
            <div class="mb-4">
                <span class="text-lg font-medium text-gray-600">{{
                    emailContactMethodOnly
                        ? t('codePromptEmailOnly')
                        : emailContactMethodFirst
                          ? t('codePromptEmail')
                          : t('codePrompt')
                }}</span>
            </div>
            <div class="flex flex-col gap-4">
                <div
                    v-if="emailContactMethodOnly"
                    class="flex flex-col gap-4"
                >
                    <login-email-field
                        ref="emailFieldRef"
                        :field-bind="emailValue"
                        :disabled="isPending"
                        :error="displayedErrors.email"
                    />
                </div>
                <div
                    v-else-if="emailContactMethodFirst"
                    class="flex flex-col gap-4"
                >
                    <login-email-field
                        ref="emailFieldRef"
                        :field-bind="emailValue"
                        :disabled="isPending"
                        :error="displayedErrors.email"
                    />
                    <span
                        class="-my-2 w-full text-center font-medium text-gray-500"
                    >
                        {{ t('purchaseContract.or') }}
                    </span>
                    <login-phone-field
                        ref="phoneFieldRef"
                        :field-bind="phoneValue"
                        :disabled="isPending"
                        :error="displayedErrors.phone"
                    />
                </div>
                <div
                    v-else
                    class="flex flex-col gap-4"
                >
                    <login-phone-field
                        ref="phoneFieldRef"
                        :field-bind="phoneValue"
                        :disabled="isPending"
                        :error="displayedErrors.phone"
                    />
                    <span
                        class="-my-2 w-full text-center font-medium text-gray-500"
                    >
                        {{ t('purchaseContract.or') }}
                    </span>
                    <login-email-field
                        ref="emailFieldRef"
                        :field-bind="emailValue"
                        :disabled="isPending"
                        :error="displayedErrors.email"
                    />
                </div>
                <div
                    v-if="submitCount > 0 && displayedErrors[''] !== undefined"
                    class="text-red-600 font-medium"
                >
                    {{ displayedErrors[''] }}
                </div>
                <div
                    v-if="!isKiosk"
                    class="flex flex-wrap gap-3"
                >
                    <div>
                        <checkbox
                            v-bind="rememberMe"
                            input-id="remember_me"
                            binary
                            class="mr-2"
                            :disabled="isPending"
                        />
                        <label
                            for="remember_me"
                            class="text-900 mr-8 font-medium"
                        >
                            {{ t('rememberMe') }}
                        </label>
                    </div>
                </div>

                <rp-hcaptcha
                    v-if="loginHcaptchaEnabled"
                    ref="hcaptchaRef"
                    :site-key="loginHcaptchaSiteKey"
                    @token="hcaptchaToken = $event"
                    @challenge-opened="hcaptchaChallengeOpened = true"
                />

                <div>
                    <div class="flex items-center gap-4">
                        <prime-button
                            v-if="showGuest"
                            outlined
                            :label="t('continueAsGuest')"
                            class="w-full"
                            :loading="isVerifyingKiosk"
                            :disabled="isPendingCode"
                            @click="handleRecaptcha"
                        />

                        <prime-button
                            type="submit"
                            :label="sendVerificationCodeButtonLabel"
                            class="w-full"
                            :loading="isPendingCode"
                            :disabled="isVerifyingKiosk"
                        />
                    </div>
                    <div
                        v-if="!emailContactMethodOnly"
                        class="mt-2 text-sm text-gray-700"
                    >
                        <i18n-t
                            keypath="otpDisclaimer"
                            scope="global"
                            tag="span"
                        >
                            <template #sendVerificationCode>
                                <span class="italic">
                                    {{ t('sendVerificationCode') }}
                                </span>
                            </template>
                            <template #terms>
                                <a
                                    class="text-primary-600 underline hover:no-underline"
                                    href="https://www.redpointhq.com/terms-of-service-sms/"
                                    >{{ t('terms') }}</a
                                >
                            </template>
                            <template #privacyPolicy>
                                <a
                                    class="text-primary-600 underline hover:no-underline"
                                    href="https://www.redpointhq.com/privacy-policy/"
                                    >{{ t('privacyPolicy') }}</a
                                >
                            </template>
                        </i18n-t>
                    </div>
                </div>
            </div>
        </form>
    </div>

    <choose-customer
        v-else-if="chooseCustomerData !== undefined"
        :choose-customer="chooseCustomerData"
        @done="done"
    />

    <date-of-birth-challenge
        v-else-if="dateOfBirthToken !== undefined"
        :token="dateOfBirthToken"
        :facility-groups="facilityGroups"
        :org-level="orgLevel"
        :age-of-majority-hint="ageOfMajorityHint"
        :online-profile-age-minimum="onlineProfileAgeMinimum"
        :use-middle-name="useMiddleName"
        @done="done"
        @multiple="chooseCustomerData = $event"
    />

    <pin-verification
        v-else
        v-model:code="twoFactorCode"
        :token="token"
        :phone="phoneValue.modelValue?.formatted"
        :email="emailValue.modelValue"
        @cancel="handlePinVerificationCancel"
        @done="done"
        @collect-date-of-birth="dateOfBirthToken = $event"
    />
</template>

<script setup lang="ts">
import { boolean, object, string } from 'yup'
import { toTypedSchema } from '@vee-validate/yup'
import {
    computed,
    inject,
    nextTick,
    onBeforeUnmount,
    onMounted,
    type Ref,
    ref,
    watch
} from 'vue'
import { useForm } from 'vee-validate'
import PinVerification from '~/components/Verification/PinVerification.vue'
import PrimeButton from 'primevue/button'
import Checkbox from 'primevue/checkbox'
import { useMutation } from '@tanstack/vue-query'
import { useRpQuery, UserReportableError } from '~/composables/graphql'
import { type FragmentType, graphql } from '~/resources/graphql'
import type {
    LoginByEmailInput,
    LoginByPhoneInput,
    ValidateKioskInput,
    VerifyGuestInput
} from '~/resources/graphql/graphql'
import { useToast } from 'primevue/usetoast'
import { useViewer } from '~/composables/use-login'
import ChooseCustomer, {
    PossibleCustomerFragment
} from '~/components/Verification/ChooseCustomer.vue'
import LoginEmailField from '~/components/Verification/LoginEmailField.vue'
import LoginPhoneField from '~/components/Verification/LoginPhoneField.vue'
import DateOfBirthChallenge from '~/components/Verification/DateOfBirthChallenge.vue'
import { useLayoutMode } from '~/composables/use-layout-mode'
import RpHcaptcha from '~/components/Verification/RpHcaptcha.vue'
import RpRecaptcha from '~/components/Verification/RpRecaptcha.vue'
import { I18nT, useI18n } from 'vue-i18n'
import { useVerifiedKiosk } from '~/composables/use-verified-kiosk'
import { useShowAlert } from '#imports'
import { useLoginModalOrganization } from '~/composables/use-login-modal-organization'

const props = withDefaults(
    defineProps<{
        orgLevel?: boolean
    }>(),
    {
        orgLevel: false
    }
)

const emit = defineEmits<{
    (e: 'close'): void
    (e: 'guest'): void
}>()

const { t, locale } = useI18n({ useScope: 'global' })
const dialogRef = inject<
    Ref<{
        data: {
            showGuest?: boolean
            ageOfMajorityHint?: number
            switchCustomerToken?: string
        }
    }>
>(
    'dialogRef',
    ref({
        data: {}
    })
)

const showGuest = computed(() => dialogRef.value.data.showGuest ?? false)
const ageOfMajorityHint = computed(
    () => dialogRef.value.data.ageOfMajorityHint ?? 0
)
const switchCustomerToken = computed(
    () => dialogRef.value.data.switchCustomerToken
)
const showRecaptcha = ref(false)
const recaptchaToken = ref('')
const hcaptchaRef = ref<InstanceType<typeof RpHcaptcha>>()
const hcaptchaToken = ref('')
const hcaptchaChallengeOpened = ref(false)
const isCollectingHcaptcha = ref(false)

const recaptchaLang = computed(() => {
    if (locale.value.startsWith('es-')) {
        return 'es'
    }

    if (locale.value.startsWith('fr-')) {
        return 'fr'
    }

    if (locale.value.startsWith('de-')) {
        return 'de'
    }

    return undefined
})

const { isKiosk } = useLayoutMode()
const { token: verifiedKioskToken, clearToken } = useVerifiedKiosk()
const token = ref<string | undefined>(switchCustomerToken.value)
const dateOfBirthToken = ref<string | undefined>(switchCustomerToken.value)
const chooseCustomerData = ref<FragmentType<typeof PossibleCustomerFragment>>()
const emailFieldRef = ref<InstanceType<typeof LoginEmailField> | null>(null)
const phoneFieldRef = ref<InstanceType<typeof LoginPhoneField> | null>(null)
const loginModalOrganization = useLoginModalOrganization()

const facilityGroups = computed(() => {
    return loginModalOrganization.value.facilityLocationGroups
})

const onlineProfileAgeMinimum = computed(() => {
    return loginModalOrganization.value.onlineProfileAgeMinimum
})

const useMiddleName = computed(() => {
    // Keep the identity challenge aligned with the organization-level CPX field preference.
    return loginModalOrganization.value.useMiddleName
})

const emailContactMethodOnly = computed(() => {
    // Email-only suppresses the SMS path entirely for orgs that should not offer phone login from CPX.
    return (
        loginModalOrganization.value.loginContactMethodDisplayOrder ===
        'email-only'
    )
})

const emailContactMethodFirst = computed(() => {
    // The setting is intentionally order-focused so future contact methods can be added without changing the modal contract.
    return (
        loginModalOrganization.value.loginContactMethodDisplayOrder ===
        'email-first'
    )
})

const primaryContactMethodIsEmail = computed(() => {
    return emailContactMethodOnly.value || emailContactMethodFirst.value
})

const loginHcaptchaEnabled = computed(() => {
    // This mirrors the server-side system setting so disabled orgs do not load
    // or execute hCaptcha before sending a LoginModal verification code.
    return loginModalOrganization.value.loginHcaptchaEnabled
})

const loginHcaptchaSiteKey = computed(() => {
    // The backend selects the public site key from the org system setting so
    // CPX renders the same hCaptcha key that the server later verifies.
    return loginModalOrganization.value.loginHcaptchaSiteKey
})

function contactMethodRequiredError() {
    // Match the validation copy to the contact methods actually offered in the modal.
    return emailContactMethodOnly.value
        ? t('codeErrorEmailOnly')
        : t('codeError')
}

function focusContactInput() {
    if (primaryContactMethodIsEmail.value) {
        emailFieldRef.value?.focus()
        return
    }

    phoneFieldRef.value?.focus()
}

const schema = toTypedSchema(
    object({
        phone: object()
            .shape({
                formatted: string(),
                number: string(),
                valid: boolean()
            })
            .test({
                message: t('customerInformation.invalidMobilePhoneWarning'),
                test: value =>
                    value.number === undefined ||
                    value.number === '' ||
                    value.valid === true
            }),
        email: string()
            .email(t('customerInformation.invalidEmailWarning'))
            .default(''),
        rememberMe: boolean().default(false)
    }).test({
        name: 'emailOrPhone',
        test: (value, context) => {
            const hasEmail = value.email.length > 0
            const hasPhone =
                !emailContactMethodOnly.value &&
                value.phone?.number !== undefined &&
                value.phone.number.length > 0

            if (hasEmail || hasPhone) {
                return true
            }

            return context.createError({
                message: contactMethodRequiredError()
            })
        }
    })
)

const {
    errors,
    defineComponentBinds,
    handleSubmit,
    resetForm,
    submitCount,
    setErrors,
    setFieldError
} = useForm({
    validationSchema: schema
})

const displayedErrors = ref<Record<string, string | undefined>>({})
let displayedErrorsTimeout: ReturnType<typeof setTimeout> | undefined

watch(
    [errors, submitCount],
    () => {
        if (displayedErrorsTimeout !== undefined) {
            clearTimeout(displayedErrorsTimeout)
        }

        if (submitCount.value === 0) {
            displayedErrors.value = {}
            return
        }

        displayedErrorsTimeout = setTimeout(() => {
            displayedErrors.value = {
                phone: errors.value.phone,
                email: errors.value.email,
                '': errors.value['']
            }
        }, 50)
    },
    { flush: 'post' }
)

onBeforeUnmount(() => {
    if (displayedErrorsTimeout !== undefined) {
        clearTimeout(displayedErrorsTimeout)
    }
})

const phoneValue = defineComponentBinds('phone', {
    validateOnModelUpdate: false,
    validateOnBlur: true
})
const emailValue = defineComponentBinds('email', {
    validateOnModelUpdate: false,
    validateOnBlur: true
})
const rememberMe = defineComponentBinds('rememberMe')

// https://stackoverflow.com/questions/11381673/detecting-a-mobile-browser
function isMobile() {
    let check = false
    ;(function (a) {
        if (
            /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
                a
            ) ||
            /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
                a.substr(0, 4)
            )
        )
            check = true
    })(navigator.userAgent || navigator.vendor || window.opera)
    return check
}

const query = useRpQuery({ orgLevel: props.orgLevel })

onMounted(() => {
    if (
        isKiosk.value === false &&
        isMobile() &&
        loginModalOrganization.value.rememberMeDefault
    ) {
        rememberMe.value['onUpdate:modelValue'](true)
    }
})

watch(
    primaryContactMethodIsEmail,
    async () => {
        await nextTick()
        focusContactInput()
    },
    { flush: 'post', immediate: true }
)

const { mutate: loginByEmail, isPending: isLoggingInByEmail } = useMutation({
    mutationFn: (input: LoginByEmailInput) =>
        query(
            graphql(/** @lang GraphQL */ `
                mutation LoginByEmail($input: LoginByEmailInput!) {
                    loginByEmail(input: $input) {
                        __typename
                        ... on LoginByEmailResult {
                            token
                        }
                        ... on RateLimitProblem {
                            message
                        }
                        ... on ValidationError {
                            fieldErrors {
                                path
                                message
                            }
                        }
                    }
                }
            `),
            {
                input
            }
        )
})

const { mutate: loginByPhone, isPending: isLoggingInByPhone } = useMutation({
    mutationFn: (input: LoginByPhoneInput) =>
        query(
            graphql(/** @lang GraphQL */ `
                mutation LoginByPhone($input: LoginByPhoneInput!) {
                    loginByPhone(input: $input) {
                        __typename
                        ... on LoginByPhoneResult {
                            token
                        }
                        ... on RateLimitProblem {
                            message
                        }
                        ... on ValidationError {
                            fieldErrors {
                                path
                                message
                            }
                        }
                    }
                }
            `),
            {
                input
            }
        )
})

function handleValidationError(
    fieldErrors: Array<{ path: string; message: string }>
) {
    const firstError = fieldErrors[0]
    if (firstError === undefined) {
        setFieldError('', t('codeError'))
        return
    }

    if (
        firstError.path === 'phone' ||
        firstError.path.endsWith('.phone') ||
        firstError.path.startsWith('phone.')
    ) {
        setFieldError('phone', firstError.message)
        return
    }

    if (
        firstError.path === 'email' ||
        firstError.path.endsWith('.email') ||
        firstError.path.startsWith('email.')
    ) {
        setFieldError('email', firstError.message)
        return
    }

    setFieldError('', firstError.message)
}

function handleUnknownLoginPayload() {
    showAlert(t('headsUp'), t('signInFlow.refreshAndTryAgain'))
}

async function collectLoginChallenge() {
    if (isKiosk.value && verifiedKioskToken.value !== '') {
        return {
            kioskToken: verifiedKioskToken.value
        }
    }

    if (!loginHcaptchaEnabled.value) {
        // The server reads the same org setting before enforcing hCaptcha, so
        // omit the token entirely when support has disabled the challenge.
        return {}
    }

    isCollectingHcaptcha.value = true

    try {
        // Invisible hCaptcha starts only after the form is valid, so customers
        // do not solve a challenge for a request we already know cannot send.
        hcaptchaChallengeOpened.value = false
        const challengeToken = await hcaptchaRef.value?.execute()
        if (challengeToken === undefined || challengeToken === '') {
            setFieldError('', t('captchaRequired'))
            return undefined
        }

        hcaptchaToken.value = challengeToken

        return {
            hcaptchaToken: challengeToken,
            hcaptchaChallengeOpened: hcaptchaChallengeOpened.value
        }
    } catch (error) {
        hcaptchaToken.value = ''
        hcaptchaChallengeOpened.value = false
        hcaptchaRef.value?.reset()

        // A dismissed or expired challenge can be attempted again immediately.
        // SDK, readiness, and timeout failures need a page refresh so hCaptcha
        // can reload cleanly instead of repeatedly using the stalled widget.
        if (
            error instanceof Error &&
            [
                'hcaptcha-closed',
                'hcaptcha-expired',
                'hcaptcha-challenge-expired'
            ].includes(error.message)
        ) {
            setFieldError('', t('captchaRequired'))
        } else {
            setFieldError('', t('signInFlow.refreshAndTryAgain'))
        }

        return undefined
    } finally {
        isCollectingHcaptcha.value = false
    }
}

function resetLoginHcaptcha() {
    hcaptchaToken.value = ''
    hcaptchaChallengeOpened.value = false
    hcaptchaRef.value?.reset()
}

function handleLoginMutationError(
    error: unknown,
    loginChallenge: {
        hcaptchaToken?: string
        hcaptchaChallengeOpened?: boolean
        kioskToken?: string
    }
) {
    resetLoginHcaptcha()

    if (
        error instanceof UserReportableError &&
        error.extensions.requiresKioskVerification === true &&
        loginChallenge.kioskToken !== undefined &&
        verifiedKioskToken.value === loginChallenge.kioskToken
    ) {
        // Network and unrelated server errors do not revoke kiosk verification.
        // A delayed rejection must also leave a newly verified credential intact.
        clearToken()
    }
}

const onSubmit = handleSubmit(async values => {
    if (displayedErrorsTimeout !== undefined) {
        clearTimeout(displayedErrorsTimeout)
    }

    setErrors({
        phone: undefined,
        email: undefined,
        '': undefined
    })
    displayedErrors.value = {}

    const shouldLoginByPhone =
        !emailContactMethodOnly.value &&
        values.phone.valid === true &&
        values.phone.number !== undefined &&
        values.phone.number.length > 0

    const loginChallenge = await collectLoginChallenge()
    if (loginChallenge === undefined) {
        return
    }

    if (shouldLoginByPhone) {
        loginByPhone(
            {
                phone: values.phone.number,
                remember: values.rememberMe,
                ...loginChallenge
            },
            {
                onSuccess: data => {
                    if (data.loginByPhone.__typename === 'ValidationError') {
                        resetLoginHcaptcha()
                        handleValidationError(
                            data.loginByPhone.fieldErrors.map(fieldError => {
                                return {
                                    path: fieldError.path,
                                    message: fieldError.message
                                }
                            })
                        )
                        return
                    }

                    if (data.loginByPhone.__typename === 'RateLimitProblem') {
                        resetLoginHcaptcha()
                        setFieldError('', data.loginByPhone.message)
                        return
                    }

                    if (data.loginByPhone.__typename === 'LoginByPhoneResult') {
                        token.value = data.loginByPhone.token
                        return
                    }

                    resetLoginHcaptcha()
                    handleUnknownLoginPayload()
                },
                onError: error =>
                    handleLoginMutationError(error, loginChallenge)
            }
        )
    } else if (values.email !== '') {
        loginByEmail(
            {
                email: values.email,
                remember: values.rememberMe,
                ...loginChallenge
            },
            {
                onSuccess: data => {
                    if (data.loginByEmail.__typename === 'ValidationError') {
                        resetLoginHcaptcha()
                        handleValidationError(
                            data.loginByEmail.fieldErrors.map(fieldError => {
                                return {
                                    path: fieldError.path,
                                    message: fieldError.message
                                }
                            })
                        )
                        return
                    }

                    if (data.loginByEmail.__typename === 'RateLimitProblem') {
                        resetLoginHcaptcha()
                        setFieldError('', data.loginByEmail.message)
                        return
                    }

                    if (data.loginByEmail.__typename === 'LoginByEmailResult') {
                        token.value = data.loginByEmail.token
                        return
                    }

                    resetLoginHcaptcha()
                    handleUnknownLoginPayload()
                },
                onError: error =>
                    handleLoginMutationError(error, loginChallenge)
            }
        )
    } else {
        // TODO: Handle error
        throw new Error()
    }
})

const toast = useToast()
const viewer = useViewer()
function done() {
    emit('close')
    toast.add({
        severity: 'success',
        summary: t('signInComplete'),
        detail: viewer.value?.name ?? '',
        life: 3000
    })
}

function handlePinVerificationCancel() {
    token.value = undefined
    twoFactorCode.value = ''
    resetForm({
        values: {
            phone: phoneValue.modelValue,
            email: emailValue.modelValue,
            rememberMe: rememberMe.modelValue
        }
    })

    nextTick(() => {
        focusContactInput()
    })
}

// Do this here so that credentials.get is 100% called before the text message is sent
const twoFactorCode = ref('')

const { mutate: verifyGuest, isPending: isVerifyingGuest } = useMutation({
    mutationFn: (input: VerifyGuestInput) =>
        query(
            graphql(/** @lang GraphQL */ `
                mutation VerifyGuest($input: VerifyGuestInput!) {
                    verifyGuest(input: $input) {
                        __typename
                        ... on VerifyGuestResult {
                            result
                        }
                    }
                }
            `),
            {
                input
            }
        )
})

const recaptchaDisabled = computed(() => {
    return recaptchaToken.value === '' && !import.meta.env.DEV
})

function submitGuest() {
    verifyGuest(
        { token: recaptchaToken.value },
        {
            onSuccess: () => {
                emit('guest')
            }
        }
    )
}

const { mutate: validateVerifiedKiosk, isPending: isVerifyingKiosk } =
    useMutation({
        mutationFn: (input: ValidateKioskInput) =>
            query(
                graphql(/** @lang GraphQL */ `
                    mutation ValidateKiosk($input: ValidateKioskInput!) {
                        validateKiosk(input: $input) {
                            __typename
                            ... on ValidateKioskResult {
                                result
                            }
                        }
                    }
                `),
                {
                    input
                }
            )
    })

const sendVerificationCodeButtonLabel = computed(() => {
    // Keep the current stage visible in screenshots so support can distinguish
    // browser-side hCaptcha stalls from pending GraphQL login requests.
    if (isCollectingHcaptcha.value) {
        return t('completingSecurityCheck')
    }

    if (isLoggingInByEmail.value || isLoggingInByPhone.value) {
        return t('sendingVerificationCode')
    }

    return t('sendVerificationCode')
})

const isPendingCode = computed(() => {
    return (
        isLoggingInByEmail.value ||
        isLoggingInByPhone.value ||
        isCollectingHcaptcha.value
    )
})

const isPending = computed(() => {
    return (
        isLoggingInByEmail.value ||
        isLoggingInByPhone.value ||
        isCollectingHcaptcha.value ||
        isVerifyingGuest.value ||
        isVerifyingKiosk.value
    )
})

const showAlert = useShowAlert()
function handleRecaptcha() {
    if (isKiosk.value && verifiedKioskToken.value !== '') {
        validateVerifiedKiosk(
            { token: verifiedKioskToken.value },
            {
                onSuccess: data => {
                    if (
                        data.validateKiosk.__typename === 'ValidateKioskResult'
                    ) {
                        if (data.validateKiosk.result) {
                            emit('guest')
                        } else {
                            clearToken()
                            showRecaptcha.value = true
                        }
                    } else {
                        showAlert(
                            t('headsUp'),
                            t('signInFlow.refreshAndTryAgain')
                        )
                    }
                }
            }
        )
    } else {
        showRecaptcha.value = true
    }
}
</script>

<style>
.p-input-icon-left,
.p-input-icon-right {
    position: relative;
    display: inline-block;
}

.p-input-icon-left > i:first-of-type {
    left: 0.75rem;
    color: #64748b;
}

.p-input-icon-left > input {
    padding-left: 2.5rem;
}

.p-input-icon-left.p-float-label > label {
    left: 2.5rem;
}

.p-input-icon-left > i,
.p-input-icon-left > svg,
.p-input-icon-right > i,
.p-input-icon-right > svg {
    position: absolute;
    top: 50%;
    margin-top: -0.5rem;
}
</style>
